import { Link, createFileRoute } from "@tanstack/react-router";
import * as z from "zod/v4";
import { zodValidator } from "@tanstack/zod-adapter";
import { useForm } from "react-hook-form";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import type { HTTPError } from "ky";
import { api } from "@/lib/http";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PasswordInput } from "@/components/ui/passwordInput";
import { generateDefaultValue } from "@/lib/utils/zod";

const resetPasswordSearchSchema = z.object({
  token: z.string().optional(),
});

export const Route = createFileRoute("/reset-password")({
  validateSearch: zodValidator(resetPasswordSearchSchema),
  component: RouteComponent,
  staticData: {
    title: "Reset Password",
  },
});

const ResetPasswordFormSchema = z
  .object({
    new_password: z
      .string("Password is required")
      .min(8, "Password must be at least 8 characters"),
    confirm_password: z
      .string("Please confirm your password")
      .min(1, "Please confirm your password"),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords do not match",
    path: ["confirm_password"],
  });
type ResetPasswordFormSchema = z.infer<typeof ResetPasswordFormSchema>;

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { token } = Route.useSearch();

  const form = useForm<ResetPasswordFormSchema>({
    resolver: standardSchemaResolver(ResetPasswordFormSchema),
    defaultValues: generateDefaultValue(ResetPasswordFormSchema),
  });

  const resetPassword = useMutation({
    mutationFn: (formData: ResetPasswordFormSchema) =>
      api.auth.updatePassword({
        token: token ?? "",
        password: formData.new_password,
      }),
    onSuccess() {
      toast.success("Password updated successfully. Please sign in.");
      navigate({ to: "/auth/signin" });
    },
    async onError(error: HTTPError) {
      const data = await error.response.json<{
        detail?: string;
        code?: string;
      }>();

      form.setError("root.serverError", {
        message: data.detail || "An unexpected error occurred",
      });
    },
  });

  if (!token) {
    return (
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center px-4 py-8">
        <div className="w-full space-y-4 text-center">
          <h1 className="text-4xl font-bold">Invalid link</h1>
          <p className="text-base text-gray-500">
            This password reset link is invalid or has expired. Please request a
            new one.
          </p>
          <Link
            to="/auth/forgot-password"
            className="inline-block text-sm font-medium text-[#dd2590] hover:underline"
          >
            Request a new link
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center px-4 py-8">
      <div className="w-full space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-4xl font-bold">Reset password</h1>
          <p className="text-base text-gray-500">
            Please enter your new password.
          </p>
        </div>

        <Form {...form}>
          <form
            className="space-y-6"
            onSubmit={form.handleSubmit((formData) => {
              if (resetPassword.isPending) return;
              resetPassword.mutate(formData);
            })}
          >
            <FormField
              control={form.control}
              name="new_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    New Password <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      autoComplete="new-password"
                      placeholder="Enter new password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirm_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Confirm Password <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      autoComplete="new-password"
                      placeholder="Enter new password again"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <button
              type="submit"
              disabled={resetPassword.isPending}
              className="mb-0 w-full rounded-md bg-[#dd2590] py-3 font-medium text-white transition-colors hover:bg-pink-700 disabled:opacity-60"
            >
              {resetPassword.isPending ? "Updating..." : "Update password"}
            </button>

            {form.formState.errors.root?.serverError ? (
              <p className="mt-2 text-sm text-destructive">
                {form.formState.errors.root.serverError.message}
              </p>
            ) : null}

            <div className="mt-2 text-center">
              <Link
                to="/auth/signin"
                className="text-sm font-medium text-[#dd2590] hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          </form>
        </Form>
      </div>
    </main>
  );
}
